/**
 * Who is asking, from the address bar.
 *
 * Anand, 23 Sep 26: "The agent home is a shareable link and I think that's not
 * best for users who sign up for the GBP growth agent, so let's make the home
 * and other linked pages first class citizens of the app."
 *
 * The same page component now has two addresses:
 *
 *   /agent/<page>/:token         shared use, no login: a WhatsApp deep link,
 *                                an owner with no account, the QA testers
 *   /app/l/:locationId/<page>    the signed-in app
 *   /app/:slug/<page>            the same, at its clean address (23 Oct 26,
 *                                lib/appAddress.ts; the slug resolves to its
 *                                location id from the in-memory slug map)
 *
 * Both talk to the same ops on agent-action-respond and agent-shareable-links.
 * The only difference is what the request is authorised by, and that is all
 * this module decides: a token call sends { token, op }, an app call sends
 * { location_id, op } with the session bearer supabase-js already attaches,
 * and the edge function resolves the session to the same home-link rights
 * (supabase/functions/_shared/session-link.ts).
 *
 * Everything here except the hook is a plain function so the rules can be
 * read and tested on their own (tests/agent/unit/agent-caller.test.ts).
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { returnToQuery } from '@/components/growth-agent/start/returnTo';
import { isLocationUuid, parseAppLocationPath } from './appAddress';

export type AgentCallerMode = 'token' | 'app';

export interface AgentCallerRef {
  mode: AgentCallerMode;
  /** The URL token, in token mode. Null inside the app. */
  token: string | null;
  /** The location the app page is open on. Null on a token page. */
  locationId: string | null;
}

/** Tokens are 8-char alphanumerics in the schema; up to 64 is accepted. */
const TOKEN_RE = /^[A-Za-z0-9]{8,64}$/;

/** Where a signed-out visitor is sent to sign in and come back. */
export const SIGN_IN_PATH = '/gbp-growth-agent/start';

/**
 * The caller behind a pathname, or null when the path is neither an agent
 * token page nor an app location page. A clean address answers only once its
 * slug is in the map (AppSlugShell renders the page after that).
 */
export function agentCallerFromPath(pathname: string): AgentCallerRef | null {
  const clean = String(pathname ?? '').split('?')[0].split('#')[0];
  const app = parseAppLocationPath(clean);
  if (app && isLocationUuid(app.locationId)) {
    return { mode: 'app', token: null, locationId: app.locationId };
  }
  const token = /^\/agent\/[^/]+\/([^/]+)/.exec(clean);
  if (token && TOKEN_RE.test(token[1])) {
    return { mode: 'token', token: token[1], locationId: null };
  }
  return null;
}

/** The page segment of either address, e.g. 'keywords'. Empty when there is none. */
export function agentPageOf(pathname: string): string {
  const clean = String(pathname ?? '').split('?')[0].split('#')[0];
  const app = parseAppLocationPath(clean);
  if (app) return app.page;
  const token = /^\/agent\/([^/]+)\//.exec(clean);
  return token ? token[1] : '';
}

/**
 * The body an op is called with. Token mode sends the token exactly as it
 * always has; app mode sends the location and lets the session say who is
 * asking.
 */
export function agentBody(
  caller: AgentCallerRef | null,
  op: string,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  if (caller?.mode === 'app' && caller.locationId) {
    return { location_id: caller.locationId, op, ...(extra ?? {}) };
  }
  return { token: caller?.token ?? '', op, ...(extra ?? {}) };
}

/**
 * The same, for agent-shareable-links, which names its ops `action`.
 */
export function agentLinkBody(
  caller: AgentCallerRef | null,
  action: string,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  if (caller?.mode === 'app' && caller.locationId) {
    return { location_id: caller.locationId, action, ...(extra ?? {}) };
  }
  return { token: caller?.token ?? '', action, ...(extra ?? {}) };
}

/**
 * The caller the page being rendered resolved, published by the hook below.
 *
 * The router is the authority on which page is open, not window.location: a
 * test renders inside a MemoryRouter, and a basename would make the two differ
 * in the browser too. The hook publishes here while its page is mounted, so a
 * plain module called from that page reads the same answer the page read.
 */
let renderedCaller: AgentCallerRef | null = null;

/**
 * A caller that a part of the page claims for itself, ahead of the address.
 *
 * The Location settings pop-up on /app and /app/connections shows one
 * location's settings while the address names no location at all. The panels
 * it composes were written for /app/l/:id pages and call agentBodyOf('', op);
 * inside the scope below they send { location_id, op } for that location, so
 * they run in app mode unchanged (docs/agent-locations-rebuild.md 7.5).
 *
 * Written only from the scope's layout effect (and its cleanup, and the test
 * seam), never while rendering: a render React throws away must not leave a
 * claim behind. The scope's children render only after the claim is live.
 */
let scopedCaller: AgentCallerRef | null = null;

/**
 * How long a closed scope keeps answering (spec Addendum A, R14): a call the
 * pop-up started (an upload that awaits a resize, a save and its follow-up
 * read) may build its body after the pop-up closed. Without this it would go
 * out as { token: '' }, since /app and /app/connections publish no caller.
 */
export const SCOPE_LINGER_MS = 60_000;

/**
 * The last scope that was live, after it closed: answers for plain modules
 * until the pathname changes, another scope claims, or SCOPE_LINGER_MS pass.
 * Read after the page's published caller, so a page that names its own
 * location always speaks for itself.
 */
let lingering: { ref: AgentCallerRef; pathname: string; until: number } | null = null;

function browserPathname(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

/** The closed scope's caller while it still lingers, else null (and it is forgotten). */
function lingeringCaller(): AgentCallerRef | null {
  if (!lingering) return null;
  if (Date.now() >= lingering.until || browserPathname() !== lingering.pathname) {
    lingering = null;
    return null;
  }
  return lingering.ref;
}

/** The scoped caller for hooks under the scope (useAgentCaller reads it). */
const AgentCallerScopeContext = createContext<AgentCallerRef | null>(null);

/**
 * Run the children as the signed-in app caller for one location. One scope at
 * a time (it sits in a modal); a second claim while one is live warns in dev.
 */
export function AgentCallerScope({ locationId, children }: { locationId: string; children?: ReactNode }) {
  const ref = useMemo<AgentCallerRef>(() => ({ mode: 'app', token: null, locationId }), [locationId]);
  // Which claim is live. Held as the ref, not a flag, so a new locationId
  // renders nothing until its own claim is made (never the old location's).
  const [liveFor, setLive] = useState<AgentCallerRef | null>(null);
  const live = liveFor === ref;
  useLayoutEffect(() => {
    if (import.meta.env.DEV && scopedCaller && scopedCaller !== ref) {
      console.warn('[agentCaller] a second AgentCallerScope claimed while another is live', {
        live: scopedCaller.locationId,
        claiming: ref.locationId,
      });
    }
    scopedCaller = ref;
    // Another scope claims: whatever a closed one left behind stops answering.
    lingering = null;
    setLive(ref);
    return () => {
      const wasLive = scopedCaller === ref;
      if (scopedCaller === ref) scopedCaller = null;
      // Only the claim that was live lingers (R14); one released out of order leaves nothing.
      if (wasLive) lingering = { ref, pathname: browserPathname(), until: Date.now() + SCOPE_LINGER_MS };
    };
  }, [ref]);
  return createElement(AgentCallerScopeContext.Provider, { value: ref }, live ? children : null);
}

/**
 * The caller for the page that is open.
 *
 * The hook below is the one a component should use. This is for the plain
 * modules that do the calling and are handed a token rather than a caller:
 * a helper in agent-post, a panel's submit function, the PWA registration.
 * It answers with what the page resolved, and falls back to the browser's own
 * address when nothing has rendered yet.
 */
export function currentAgentCaller(): AgentCallerRef {
  if (scopedCaller) return scopedCaller;
  if (renderedCaller) return renderedCaller;
  const closedScope = lingeringCaller();
  if (closedScope) return closedScope;
  return addressCaller();
}

function addressCaller(): AgentCallerRef {
  return agentCallerFromPath(browserPathname()) ?? { mode: 'token', token: null, locationId: null };
}

/**
 * The caller a body is built for. A closed scope lingers only for a call that
 * carries no token of its own (the pop-up's panels send ''): a part of the
 * page that holds a real token (the Overview tab's roll-ups) keeps sending it,
 * never the closed pop-up's location.
 */
function bodyCaller(token: string | null | undefined): AgentCallerRef {
  const caller = currentAgentCaller();
  if (token && lingering && caller === lingering.ref) return addressCaller();
  return caller;
}

/** Test seam: forget what the last render published, and any scope's claim, live or lingering. */
export function resetAgentCaller(): void {
  renderedCaller = null;
  scopedCaller = null;
  lingering = null;
}

/**
 * The body for an op, for whoever is calling: the token when the page has
 * one, the location when it is an app page. `token` is the token the caller
 * already held; inside the app there is none and the session speaks instead.
 */
export function agentBodyOf(
  token: string | null | undefined,
  op: string,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  const caller = bodyCaller(token);
  if (caller.mode === 'app' && caller.locationId) {
    return { location_id: caller.locationId, op, ...(extra ?? {}) };
  }
  return { token: token ?? caller.token ?? '', op, ...(extra ?? {}) };
}

/** The same, for agent-shareable-links, which names its ops `action`. */
export function agentLinkBodyOf(
  token: string | null | undefined,
  action: string,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  const caller = bodyCaller(token);
  if (caller.mode === 'app' && caller.locationId) {
    return { location_id: caller.locationId, action, ...(extra ?? {}) };
  }
  return { token: token ?? caller.token ?? '', action, ...(extra ?? {}) };
}

/**
 * The same, for agent-crm. That function already knows two callers: a share
 * token, and a signed-in member whose body names the location. Inside the app
 * it is the second of those, so nothing on the server had to change.
 */
export function agentCrmBodyOf(
  token: string | null | undefined,
  op: string,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  const caller = bodyCaller(token);
  if (caller.mode === 'app' && caller.locationId) {
    return { op, location_id: caller.locationId, ...(extra ?? {}) };
  }
  return { op, token: token ?? caller.token ?? '', ...(extra ?? {}) };
}

/**
 * The same, for agent-post-image. It names its token `token` like the ops
 * above but takes no `op`, so the picture the wizard asks for travels on its
 * own (Manu, QA note 8f0ecdad, 24 Sep 26: inside the app there was no token
 * to send and every request came back 401).
 */
export function agentImageBodyOf(
  token: string | null | undefined,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  const caller = bodyCaller(token);
  if (caller.mode === 'app' && caller.locationId) {
    return { location_id: caller.locationId, ...(extra ?? {}) };
  }
  return { token: token ?? caller.token ?? '', ...(extra ?? {}) };
}

/**
 * The same, for save-wizard-content-idea, which names its token `shareToken`
 * because the client content generator reaches it too. That surface is not an
 * agent page, so it keeps sending its own share token and never lands here in
 * app mode.
 */
export function wizardSaveBodyOf(
  token: string | null | undefined,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  const caller = bodyCaller(token);
  if (caller.mode === 'app' && caller.locationId) {
    return { location_id: caller.locationId, ...(extra ?? {}) };
  }
  return { shareToken: token ?? caller.token ?? '', ...(extra ?? {}) };
}

/** The status supabase-js hides inside a FunctionsHttpError. */
export function errorStatus(error: unknown): number | null {
  const status = (error as { context?: { status?: unknown } } | null)?.context?.status;
  return typeof status === 'number' ? status : null;
}

/** A refusal the app answers by asking the person to sign in again. */
export function isSignInError(error: unknown): boolean {
  const status = errorStatus(error);
  return status === 401 || status === 403;
}

/** Where to send them, with the page they wanted kept for the way back. */
export function signInPath(returnTo: string): string {
  return `${SIGN_IN_PATH}?${returnToQuery(returnTo || '/app')}`;
}

export interface AgentCaller extends AgentCallerRef {
  /** A body for agent-action-respond. */
  body: (op: string, extra?: Record<string, unknown>) => Record<string, unknown>;
  /** A body for agent-shareable-links. */
  linkBody: (action: string, extra?: Record<string, unknown>) => Record<string, unknown>;
  /**
   * Handle a refusal. In the app a 401 or 403 means the session is gone or
   * this location is not theirs, so they go to the sign-in page and come back
   * here. Returns true when it took over, so the caller can stop.
   */
  handleAuthError: (error: unknown) => boolean;
}

/**
 * The caller for the page being rendered. Works anywhere inside the route, so
 * a panel deep in the tree can ask for it instead of being handed a token.
 */
export function useAgentCaller(): AgentCaller {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  // Inside an AgentCallerScope the scope speaks, not the address.
  const scoped = useContext(AgentCallerScopeContext);
  // Held by what the address resolves to and its page, not by the pathname
  // itself: an old /app/l/<id>/<page> moving to /app/<slug>/<page> is the same
  // caller, so nothing keyed on it reads again (clean addresses, 23 Oct 26).
  // A different page is still a new caller, as before.
  const fromPath = agentCallerFromPath(pathname);
  const mode: AgentCallerMode = fromPath?.mode ?? 'token';
  const token = fromPath?.token ?? null;
  const locationId = fromPath?.locationId ?? null;
  const page = agentPageOf(pathname);
  const ref = useMemo<AgentCallerRef>(
    () => scoped ?? { mode, token, locationId },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- page: a different page is a new caller, as before
    [scoped, mode, token, locationId, page],
  );
  // The plain modules that post on this page's behalf read this (agentBodyOf).
  // Published while the page is mounted and taken down with it, so a page
  // outside the agent surface never inherits the last agent page's answer.
  // Under a scope it publishes nothing: the scope already answers for the
  // plain modules, and the page around it keeps its own published caller.
  useEffect(() => {
    if (scoped) return undefined;
    renderedCaller = ref;
    return () => {
      if (renderedCaller === ref) renderedCaller = null;
    };
  }, [scoped, ref]);

  // The address to come back to after signing in, and the navigate that
  // takes them there, are read when a refusal happens, not when the caller is
  // made. Holding them in a ref keeps handleAuthError (and so the caller)
  // the same object for as long as the page is: it used to change on every
  // query-string change, and every loader keyed on the caller refetched the
  // whole page when a tab or a filter moved (redesign contract, 29 Sep 26).
  const returnTo = useRef({ navigate, path: `${pathname}${location.search ?? ''}` });
  returnTo.current = { navigate, path: `${pathname}${location.search ?? ''}` };
  const handleAuthError = useCallback(
    (error: unknown): boolean => {
      if (ref.mode !== 'app' || !isSignInError(error)) return false;
      returnTo.current.navigate(signInPath(returnTo.current.path), { replace: true });
      return true;
    },
    [ref.mode],
  );

  return useMemo<AgentCaller>(
    () => ({
      ...ref,
      body: (op, extra) => agentBody(ref, op, extra),
      linkBody: (action, extra) => agentLinkBody(ref, action, extra),
      handleAuthError,
    }),
    [ref, handleAuthError],
  );
}
